import type { MenuItem, ModificationGroup } from "@/features/menu/menuSlice";
import { hydrateFromOrder } from "./cartSlice";
import type {
  CartHydrationMode,
  CartModifierGroup,
  BackendCartItem,
} from "./cartSlice";

export interface OrderToCartMenuLookup {
  items: MenuItem[];
  itemsById: Record<string, MenuItem>;
  modificationsById: Record<string, ModificationGroup>;
}

export interface CartAddItemPayload {
  menuItemId: string;
  name: string;
  basePriceCents: number;
  quantity: number;
  imageUrl?: string;
  caloriesLabel?: string;
  modifiers?: CartModifierGroup[];
  backendItem: BackendCartItem;
}

/** A stored modifier, resolved to the group and option the menu serves now. */
interface ResolvedOption {
  readonly groupId: string;
  readonly groupName: string;
  readonly optionId: string;
  readonly optionName: string;
  readonly priceCents: number;
}

/**
 * A stored modifier (`optionGroup.guid` + `item.guid`) against what the item
 * serves now. An item with V2 groups (FOYR-565) is answered by them alone:
 * an option's `priceCents` is the value the backend prices it at, and an
 * option missing from them is not served, even if a legacy group still
 * lists it. Only an item with no V2 groups (a pre-V2 line) falls back to the
 * restaurant's legacy groups. Null when not served: the option adds nothing
 * to the price and keeps its stored name.
 */
function resolveStoredOption(
  menuItem: MenuItem | undefined,
  menu: OrderToCartMenuLookup,
  groupGuid: string,
  optionGuid: string,
): ResolvedOption | null {
  const v2Groups = menuItem?.modifierGroups ?? [];
  if (v2Groups.length > 0) {
    const v2Group = v2Groups.find((g) => g._id === groupGuid);
    const v2Option = v2Group?.options.find((o) => o._id === optionGuid);
    if (!v2Group || !v2Option) return null;
    return {
      groupId: v2Group._id,
      groupName: v2Group.name,
      optionId: v2Option._id,
      optionName: v2Option.name,
      priceCents: v2Option.priceCents,
    };
  }
  const legacy = menu.modificationsById[groupGuid];
  const opt = legacy?.data?.find(
    (o) => o.posId === optionGuid || o.id === optionGuid,
  );
  if (!legacy || !opt) return null;
  return {
    groupId: legacy.mod_id,
    groupName: legacy.displayName ?? legacy.name ?? "",
    optionId: opt.id,
    optionName: opt.displayName,
    priceCents: Number(opt.prices?.[0]?.price ?? 0),
  };
}

/**
 * Derives cart line-item payloads (ready for cartSlice's `addItem`) from a
 * reservation's already-placed order items, resolving item/modifier guids
 * against the currently-loaded menu.
 *
 * Extracted from PreorderHome's "Edit Order" cart-seeding logic so the same
 * derivation backs both the manual Edit Order flow and automatic cart
 * hydration on reservation load — the two must never diverge, since a
 * divergence would let a guest submit a cart that doesn't match what
 * "Edit Order" would have produced for the same order.
 *
 * An order item whose stored guid no longer resolves against the current menu
 * (item removed from the menu entirely) is still included, using its raw
 * guid as a fallback name and $0 as a fallback price — the same graceful
 * degradation PreorderHome has always used for this case. Dropping it here
 * instead would silently remove it from the guest's real order the next
 * time the cart is submitted through the edit path, since editOrder
 * replaces the order wholesale rather than merging.
 */
export function buildCartItemsFromOrder(
  orderItems: unknown[],
  menu: OrderToCartMenuLookup,
): CartAddItemPayload[] {
  const items = Array.isArray(orderItems) ? orderItems : [];

  return items.map((raw) => {
    const it = raw as any;
    const itemGuid = String(it?.item?.guid ?? "");
    // A stored guid is the menu item's `_id` (written since FOYR-424) or, for
    // an older order, its POS id; itemsById is indexed by both.
    const menuItem =
      menu.itemsById[itemGuid] || menu.items.find((m) => m.id === itemGuid);
    const name = menuItem?.name || itemGuid || "Item";
    const basePriceCents = menuItem?.priceCents || 0;
    const qty = Number(it?.quantity ?? 1);
    const backendMods = Array.isArray(it?.modifiers) ? it.modifiers : [];
    const resolved = backendMods.map((m: any) =>
      resolveStoredOption(
        menuItem,
        menu,
        String(m?.optionGroup?.guid),
        String(m?.item?.guid ?? ""),
      ),
    );
    // Every stored option takes part in the line's identity (computeLineId),
    // served or not. One the menu no longer serves keeps its stored guids
    // and name and adds nothing to the price; dropping it would let two
    // lines that differ only in such options merge, and the next replacing
    // PUT would write the first one's options for both. Sent back as is,
    // the server refuses it (FOYR-565 option_not_served) and Place marks
    // the line for the guest to remove or re-choose.
    const modifiersGroups = backendMods.reduce(
      (groups: CartModifierGroup[], m: any, index: number) => {
        const match = resolved[index];
        const optionGuid = String(m?.item?.guid ?? "");
        if (!match && !optionGuid) return groups;
        const groupId = match?.groupId ?? String(m?.optionGroup?.guid ?? "");
        const existingGroup = groups.find((g) => g.mod_id === groupId);
        const option = {
          id: match?.optionId ?? optionGuid,
          displayName:
            match?.optionName ??
            (typeof m?.item?.name === "string" ? m.item.name : ""),
          priceCents: match?.priceCents ?? 0,
          quantity: Number(m?.quantity ?? 1),
        };
        if (existingGroup) {
          existingGroup.options.push(option);
        } else {
          groups.push({
            mod_id: groupId,
            displayName:
              match?.groupName ??
              (typeof m?.optionGroup?.name === "string" ? m.optionGroup.name : ""),
            options: [option],
          });
        }
        return groups;
      },
      [] as CartModifierGroup[],
    );

    const backendItem: BackendCartItem = {
      itemGroup: (menuItem as any)?.posCategoryIds?.[0]
        ? {
            guid: String((menuItem as any).posCategoryIds[0]),
          }
        : undefined,
      // FOYR-424 R15: the line identity is always the menu item's `_id`, so
      // an older order stored under POS ids is rewritten on its next edit.
      // An item no longer on the menu (or one from a menu persisted before
      // `menuItemId` existed) keeps its stored guid verbatim, so a replacing
      // PUT sends back what is stored rather than "undefined".
      item: {
        guid: menuItem?.menuItemId || itemGuid,
        name,
      },
      quantity: qty,
      modifiers: backendMods.map((m: any, index: number) => {
        const match = resolved[index];
        const group =
          menu.modificationsById[String(m?.optionGroup?.guid)] || undefined;
        const optId = String(m?.item?.guid ?? "");
        return {
          // Stored modifiers carry the POS option guid in item.guid (as
          // SingleItemPage writes them) and no `pos` key. Round-trip the
          // stored guid and names; the menu only refreshes the names when
          // the group happens to be loaded.
          optionGroup: {
            guid: String(m?.optionGroup?.guid),
            name:
              match?.groupName ??
              group?.displayName ??
              group?.name ??
              m?.optionGroup?.name ??
              "",
          },
          item: {
            guid: optId,
            name: match?.optionName ?? m?.item?.name ?? "",
          },
          quantity: Number(m?.quantity ?? 1),
          modifiers: Array.isArray(m?.modifiers) ? m.modifiers : [],
        };
      }),
    };

    return {
      // The menu's own id, not the stored guid: the stored guid may be the
      // `_id` while menu items (and every line added from the menu) key on
      // `id`. Keying on the stored guid would split one item into two cart
      // lines and hide it from the menu's quantity badges.
      menuItemId: menuItem?.id || itemGuid,
      name,
      basePriceCents,
      quantity: qty,
      imageUrl: menuItem?.imageUrl,
      caloriesLabel: menuItem?.caloriesLabel,
      modifiers: modifiersGroups.length ? modifiersGroups : undefined,
      backendItem,
    };
  });
}

/**
 * Fingerprint of a stored order's items. The cart records the key of the
 * order it was hydrated from; a cart whose key doesn't match the
 * reservation's current order does not hold that order (FOYR-469).
 */
export function orderSourceKey(orderItems: readonly unknown[]): string {
  return JSON.stringify(orderItems);
}

/** Whether a cart holding `cartKey` was hydrated from exactly these stored items. */
export function isCartHydratedFromOrder(
  cartKey: string | undefined,
  orderItems: readonly unknown[],
): boolean {
  return cartKey === orderSourceKey(orderItems);
}

/**
 * The cart action that hydrates from a stored order - the one path shared
 * by page load, "Edit Order", "Add more" and the replacing-PUT guard, all
 * derived through buildCartItemsFromOrder.
 */
export function hydrateCartFromOrder(
  orderItems: unknown[],
  menu: OrderToCartMenuLookup,
  mode: CartHydrationMode,
) {
  return hydrateFromOrder({
    items: buildCartItemsFromOrder(orderItems, menu),
    sourceKey: orderSourceKey(orderItems),
    mode,
  });
}
